import React, { useCallback, useEffect, useRef, useState } from "react";
import CloudSyncOutlined from "@mui/icons-material/CloudSyncOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import IndexProgressDialog from "../IndexProgressDialog";
import { usePermissions } from "../../Context/permissionsProvider";
import { useUI } from "../../Context/UIContext";
import {
  getDataFreshnessStatus,
  getNextRuntimeStatus,
  syncNextDataFreshness,
  refreshCategoriesCache,
  refreshSelectedProductsCache,
  refreshVendorsCache,
  refreshBannersCache,
} from "../../../services/cacheSyncService";
import { getIndexStatus, postRefreshIndex } from "../../../services/reindexService";

const REINDEX_POLL_MS = 2000;

const formatEpoch = (value) => {
  if (value == null || value === "") {
    return "—";
  }
  const date = new Date(Number(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("it-IT");
};

const formatNextRuntimeError = (message) => {
  if (!message || typeof message !== "string") {
    return "AdminService non riesce a recuperare il runtime Next (POST /internal/runtime-status via NEXT_INTERNAL_URL, es. http://fe-application:31000).";
  }
  return message;
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "—";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const formatCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "—";
};

const RICERCA_SCOPE_LABELS = {
  categoryTree: "Albero categorie",
  selectedProducts: "Prodotti selezionati",
  vendors: "Dati vendor",
  paymentMethods: "Metodi di pagamento",
};

const NEXT_DATA_FRESHNESS_LABELS = {
  esIndex: "Indicizzazione prodotti",
  categoryTree: "Albero categorie",
  selectedProducts: "Prodotti selezionati",
  vendors: "Vendor e metodi pagamento",
  banners: "Hero banners",
};

const LOADER_CACHE_LABELS = {
  categoryCatalog: "Albero categorie",
  heroBanners: "Hero banners",
  landingSearchPool: "Pool landing ricerca",
  partnerDetail: "Dettaglio partner",
  partnersSearch: "Ricerca partner",
  productDetail: "Scheda prodotto (PDP)",
  searchResults: "Risultati ricerca",
  selectedProducts: "Prodotti selezionati",
  vendorsCatalog: "Vendor e metodi pagamento",
};

const normalizeLoaderCacheRow = (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }
  const id = row.id ?? row.scope ?? "";
  return {
    id,
    label: LOADER_CACHE_LABELS[id] ?? row.label ?? id,
    entries: row.entryCount ?? row.entries ?? 0,
    maxEntries: row.maxEntries,
    estimatedPayloadBytes: row.estimatedPayloadBytes ?? row.estimatedBytes ?? 0,
  };
};

const normalizeRicercaMetricRow = (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }
  const scope = row.scope ?? row.label ?? "";
  return {
    scope,
    label: RICERCA_SCOPE_LABELS[scope] ?? row.label ?? scope,
    entryCount: row.entryCount ?? row.entries ?? 0,
    estimatedBytes: row.estimatedBytes ?? row.estimatedPayloadBytes ?? 0,
  };
};

function FreshnessRefreshButton({ label, loading, disabled, onClick }) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          disabled={disabled || loading}
          onClick={onClick}
          sx={{
            color: "white",
            backgroundColor: "#0d4697",
            "&:hover": {
              backgroundColor: "#082e64",
            },
          }}
        >
          {loading ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

const RuntimeNextPanel = ({ runtime }) => {
  if (!runtime) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nessun dato runtime disponibile.
      </Typography>
    );
  }

  const isUnavailable = runtime.available === false
    || (runtime.available == null && runtime.uptimeSeconds == null && runtime.error);

  if (isUnavailable) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {formatNextRuntimeError(runtime.error)}
      </Alert>
    );
  }

  const memory = runtime.memory ?? {};
  const cacheSummary = runtime.cacheSummary ?? {};
  const processInfo = runtime.process ?? {};
  const loaderCaches = (Array.isArray(runtime.loaderCaches) ? runtime.loaderCaches : [])
    .map(normalizeLoaderCacheRow)
    .filter(Boolean);
  const loaderBytes = cacheSummary.loaderCachesEstimatedBytes ?? memory.loaderCachesEstimatedBytes ?? 0;
  const loaderRowsWithData = loaderCaches.filter(
    (row) => Number(row.entries) > 0 || Number(row.estimatedPayloadBytes) > 0,
  );
  const nextFreshness = runtime.dataFreshness ?? {};
  const nextFreshnessRows = Object.keys(NEXT_DATA_FRESHNESS_LABELS).map((key) => ({
    key,
    label: NEXT_DATA_FRESHNESS_LABELS[key],
    value: nextFreshness[key],
  }));

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Raccolta: {formatEpoch(runtime.collectedAt)} · Uptime {runtime.uptimeSeconds ?? "—"} s · PID {processInfo.pid ?? "—"} ·{" "}
        {processInfo.env ?? "—"}
      </Typography>

      <Typography variant="subtitle2">Versioni dati interne (Next server)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Ultimo push Ricerca applicato: {formatEpoch(runtime.dataFreshnessLastAppliedAt)}
        {" · "}
        Ultimo allineamento da Ricerca (avvio server): {formatEpoch(runtime.dataFreshnessLastSyncedFromBackendAt)}
        {" · "}
        Se i timestamp non avanzano, verifica GATEWAY_INTERNAL_URL / NEXT_INTERNAL_URL e INTERNAL_AUTH_SECRET.
      </Typography>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            <TableCell>Ultimo aggiornamento (epoch ms)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nextFreshnessRows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>{row.label}</TableCell>
              <TableCell>{formatEpoch(row.value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="subtitle2">Memoria processo</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Metrica</TableCell>
            <TableCell align="right">Valore</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>RSS</TableCell>
            <TableCell align="right">{formatBytes(cacheSummary.processRssBytes ?? memory.rssBytes)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Heap usato</TableCell>
            <TableCell align="right">{formatBytes(cacheSummary.processHeapUsedBytes ?? memory.heapUsedBytes)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Payload cache loader (stima)</TableCell>
            <TableCell align="right">{formatBytes(loaderBytes)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography variant="subtitle2">Cache loader (memoria)</Typography>
      {loaderRowsWithData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessuna entry in cache con payload. Visita il sito e aggiorna; dopo un bump di versione le nuove chiavi si popolano al prossimo hit (le entry vecchie decadono da sole).
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cache</TableCell>
              <TableCell align="right">Entry</TableCell>
              <TableCell align="right">Max</TableCell>
              <TableCell align="right">Stima</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderRowsWithData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{formatCount(row.entries)}</TableCell>
                <TableCell align="right">{formatCount(row.maxEntries)}</TableCell>
                <TableCell align="right">{formatBytes(row.estimatedPayloadBytes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
};

const Sincronizzazione = () => {
  const { featureFlags } = usePermissions();
  const { showError, showLoading, hideOverlay, showSuccess } = useUI();
  const [freshness, setFreshness] = useState(null);
  const [nextRuntime, setNextRuntime] = useState(null);
  const [indexDialogOpen, setIndexDialogOpen] = useState(false);
  const [indexMinimized, setIndexMinimized] = useState(false);
  const [indexStatus, setIndexStatus] = useState(null);
  const [nextRuntimeLoading, setNextRuntimeLoading] = useState(false);
  const [nextSyncLoading, setNextSyncLoading] = useState(false);
  const [freshnessRefreshKey, setFreshnessRefreshKey] = useState(null);
  const indexPollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    const [dataStatus, runtime] = await Promise.all([
      getDataFreshnessStatus().catch(() => null),
      getNextRuntimeStatus().catch(() => null),
    ]);
    setFreshness(dataStatus);
    setNextRuntime(runtime?.next ?? null);
  }, []);

  const loadNextRuntime = useCallback(async () => {
    setNextRuntimeLoading(true);
    try {
      const runtime = await getNextRuntimeStatus();
      setNextRuntime(runtime?.next ?? null);
    } catch (error) {
      showError(error?.message || "Errore durante l'aggiornamento runtime Next.");
    } finally {
      setNextRuntimeLoading(false);
    }
  }, [showError]);

  const handleForceNextCacheSync = useCallback(async () => {
    setNextSyncLoading(true);
    try {
      showLoading("Sincronizzazione cache Next...");
      const result = await syncNextDataFreshness();
      hideOverlay();
      if (result?.synced === false) {
        showError(result?.error || "Sincronizzazione cache Next non riuscita.");
        return;
      }
      showSuccess("Cache Next allineate a Ricerca e loader invalidati.");
      await loadStatus();
    } catch (error) {
      hideOverlay();
      showError(error?.message || "Errore durante la sincronizzazione cache Next.");
    } finally {
      setNextSyncLoading(false);
    }
  }, [hideOverlay, loadStatus, showError, showLoading, showSuccess]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const stopIndexPolling = () => {
    if (indexPollRef.current) {
      clearInterval(indexPollRef.current);
      indexPollRef.current = null;
    }
  };

  const pollIndexStatus = async () => {
    try {
      const status = await getIndexStatus();
      setIndexStatus(status);
      if (!status.running) {
        stopIndexPolling();
        loadStatus();
      }
    } catch {
      stopIndexPolling();
      setIndexStatus({ running: false, phase: "FAILED", percentage: 0, processed: 0, total: 0 });
    }
  };

  const startIndexPolling = () => {
    stopIndexPolling();
    pollIndexStatus();
    indexPollRef.current = setInterval(pollIndexStatus, REINDEX_POLL_MS);
  };

  const handleFullReindex = async () => {
    if (!featureFlags.enableSincronizzazioneIndicizzazione) {
      return;
    }
    setIndexStatus({ running: true, phase: "INDEXING", percentage: 0, processed: 0, total: 0 });
    setIndexMinimized(false);
    setIndexDialogOpen(true);
    try {
      await postRefreshIndex();
    } catch {
      const current = await getIndexStatus().catch(() => null);
      if (!current?.running) {
        setIndexDialogOpen(false);
        showError("Errore durante l'avvio dell'indicizzazione.");
        return;
      }
    }
    startIndexPolling();
  };

  const runAction = async (label, action) => {
    try {
      showLoading(`${label}...`);
      await action();
      hideOverlay();
      showSuccess(`${label} completata`);
      await loadStatus();
    } catch (error) {
      hideOverlay();
      showError(error?.message || `Errore durante: ${label}`);
    }
  };

  useEffect(() => () => stopIndexPolling(), []);

  const freshnessRows = [
    {
      key: "esIndex",
      label: "Indicizzazione prodotti",
      canRefresh: featureFlags.enableSincronizzazioneIndicizzazione,
      refreshLabel: "Rigenera indice prodotti",
      onRefresh: handleFullReindex,
    },
    {
      key: "categoryTree",
      label: "Albero categorie",
      canRefresh: featureFlags.enableSincronizzazioneCacheCategorie,
      refreshLabel: "Rigenera albero categorie",
      onRefresh: () => runAction("Rigenera categorie", refreshCategoriesCache),
    },
    {
      key: "selectedProducts",
      label: "Prodotti selezionati",
      canRefresh: featureFlags.enableSincronizzazioneProdottiScelti,
      refreshLabel: "Rigenera prodotti scelti",
      onRefresh: () => runAction("Rigenera prodotti scelti", refreshSelectedProductsCache),
    },
    {
      key: "vendors",
      label: "Dati vendor",
      canRefresh: true,
      refreshLabel: "Rigenera anagrafica vendor",
      onRefresh: () => runAction("Rigenera vendor", refreshVendorsCache),
    },
    {
      key: "banners",
      label: "Hero banners",
      canRefresh: true,
      refreshLabel: "Invalida hero sul sito",
      onRefresh: () => runAction("Invalida hero banner", refreshBannersCache),
    },
  ];

  const ricercaCacheMetrics = (Array.isArray(freshness?.cacheMetrics) ? freshness.cacheMetrics : [])
    .map(normalizeRicercaMetricRow)
    .filter(Boolean);
  const ricercaFreshnessAvailable = freshness?.available !== false;

  const handleFreshnessRegenerate = async (row) => {
    if (!row.canRefresh) {
      return;
    }
    setFreshnessRefreshKey(row.key);
    try {
      await row.onRefresh();
    } finally {
      setFreshnessRefreshKey(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Sincronizzazione
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Versioni Ricerca e runtime Next: on-demand via Gateway → AdminService (apertura pagina o pulsante aggiorna). Ricerca invia le versioni a Next dopo ogni re-cache; il sito allinea server e client (meta / localStorage).
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" component="h2">
              Data ultimi aggiornamenti
            </Typography>
          </Box>
          {freshness?.available === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {freshness?.error
                || "AdminService non riesce a leggere le versioni da Ricerca. Verifica Gateway, ms-ricerca (endpoint /internal/data-status) e ricostruisci l'immagine se necessario."}
            </Alert>
          )}
          <Table size="small">
            <TableBody>
              {freshnessRows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 500, width: "42%" }}>
                    {row.label}
                  </TableCell>
                  <TableCell>{formatEpoch(freshness?.[row.key])}</TableCell>
                  <TableCell align="right" width={56}>
                    <FreshnessRefreshButton
                      label={row.refreshLabel}
                      loading={freshnessRefreshKey === row.key}
                      disabled={!row.canRefresh}
                      onClick={() => handleFreshnessRegenerate(row)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {ricercaFreshnessAvailable && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="h6" component="h2">
                Metriche cache in MS_RicercaProdotti
              </Typography>
            </Box>
            {ricercaCacheMetrics.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna metrica cache disponibile. Usa il pulsante aggiorna o verifica ms-ricerca.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scope</TableCell>
                    <TableCell align="right">Entry</TableCell>
                    <TableCell align="right">Stima</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ricercaCacheMetrics.map((row) => (
                    <TableRow key={row.scope}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{formatCount(row.entryCount)}</TableCell>
                      <TableCell align="right">{formatBytes(row.estimatedBytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6" component="h2">
              Metriche next server
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Forza allineamento versioni Ricerca su Next e invalida cache loader in memoria">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Forza invalidazione cache Next server"
                    disabled={nextSyncLoading || nextRuntimeLoading}
                    onClick={handleForceNextCacheSync}
                    sx={{
                      color: "white",
                      backgroundColor: "#1565c0",
                      "&:hover": {
                        backgroundColor: "#0d4697",
                      },
                    }}
                  >
                    {nextSyncLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CloudSyncOutlined fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Aggiorna metriche next server">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Aggiorna metriche next server"
                    disabled={nextRuntimeLoading || nextSyncLoading}
                    onClick={loadNextRuntime}
                    sx={{
                      color: "white",
                      backgroundColor: "#0d4697",
                      "&:hover": {
                        backgroundColor: "#082e64",
                      },
                    }}
                  >
                    {nextRuntimeLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <RefreshOutlined fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          <RuntimeNextPanel runtime={nextRuntime} />
        </CardContent>
      </Card>

      <IndexProgressDialog
        open={indexDialogOpen}
        minimized={indexMinimized}
        status={indexStatus}
        onClose={() => {
          if (indexStatus?.running) {
            setIndexDialogOpen(false);
            setIndexMinimized(true);
            return;
          }
          stopIndexPolling();
          setIndexDialogOpen(false);
          setIndexMinimized(false);
        }}
        onExpand={() => {
          setIndexMinimized(false);
          setIndexDialogOpen(true);
        }}
        onDismiss={() => {
          stopIndexPolling();
          setIndexMinimized(false);
          setIndexDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default Sincronizzazione;
